<template>
  <div
    class="ingredient-attachments-view"
    data-cy="ingredient-attachments-view"
  >
    <div
      v-if="existingAttachments.length"
      class="ingredient-attachments-view__container"
    >
      <div
        class="ingredient-attachments-view__header"
        data-cy="ingredient-attachments-header"
      >
        <h3>Uploaded Attachments</h3>
      </div>
      <div class="ingredient-attachments-view__attachments">
        <div
          v-for="attachment in existingAttachments"
          :key="attachment.Name"
          class="ingredient-attachments-view__attachment"
          data-cy="ingredient-attachment"
        >
          <LoadingButton
            :loading="downloadingAttachment === attachment"
            type="button"
            theme-color="primary"
            @click="downloadAttachmentAsync(attachment)"
            data-cy="download-attachment-button"
            :disabled="
              downloadingAttachment != null &&
              downloadingAttachment !== attachment
            "
          >
            {{ attachment.Name }}{{ attachment.DocType }}
          </LoadingButton>
          <div
            class="ingredient-attachments-view__attachment__date"
            data-cy="attachment-date"
          >
            {{
              attachment.Modified &&
              new Date(attachment.Modified).toLocaleString()
            }}
          </div>
          <div class="ingredient-attachments-view__attachment__actions">
            <Button
              v-if="attachment.AttachmentType === 'Saved'"
              type="button"
              theme-color="error"
              @click="deleteAttachmentAsync(attachment)"
              data-cy="delete-attachment-button"
            >
              Delete
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { onBeforeMount, ref } from "vue";
import { Button } from "@progress/kendo-vue-buttons";
import type { EirSharePointAttachment } from "@/Domain/EirSharePointAttachment";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import { useNotificationStore } from "@/stores/useNotificationStore";
import LoadingButton from "@/shared/ui/inputs/LoadingButton.vue";
import type { Attachment } from "@/Domain/Attachment";
import moment from "moment";

const notificationStore = useNotificationStore();

interface FormAttachement {
  Name: string;
  FileUrl: string;
  DocType: string;
  Data: number[];
  Modified: string;
  AttachmentType: "Submitted" | "Saved";
}

const props = defineProps<{
  visitId: number;
}>();

const existingAttachments = ref<FormAttachement[]>([]);
const downloadingAttachment = ref<FormAttachement | null>(null);

const updateFormAttachmentsAsync = async function () {
  const submittedPromise = handleResponsePromiseAsync(
    api.get("GetEirUploadedDocs", {
      visitId: props.visitId,
    }),
    (data) => {
      const attachments = data.map((attachment: EirSharePointAttachment) => ({
        Name: attachment.Title,
        FileUrl: attachment.FileUrl,
        DocType: "",
        Data: [] as number[],
        Modified: attachment.Modified
          ? moment.utc(attachment.Modified).toString()
          : "",
        AttachmentType: "Submitted",
      })) as FormAttachement[];
      existingAttachments.value = [
        ...existingAttachments.value,
        ...attachments,
      ];
    },
    "FormAttachmentsView.vue",
    "updateFormAttachmentsAsync"
  );

  const savedPromise = handleResponsePromiseAsync(
    api.get("GetSavedEirUploadedDocs", {
      visitId: props.visitId,
    }),
    (data) => {
      const attachments = data.map((attachment: Attachment) => ({
        Name: attachment.Name,
        FileUrl: attachment.FullPathName,
        DocType: attachment.DocType,
        Data: attachment.Data,
        Modified: attachment.Created?.toString() ?? "",
        AttachmentType: "Saved",
      })) as FormAttachement[];
      existingAttachments.value = [
        ...existingAttachments.value,
        ...attachments,
      ];
    },
    "FormAttachmentsView.vue",
    "updateFormAttachmentsAsync"
  );

  await Promise.all([submittedPromise, savedPromise]);
};

const downloadAttachmentAsync = async function (attachment: FormAttachement) {
  downloadingAttachment.value = attachment;
  try {
    if (attachment.AttachmentType === "Saved") {
      const response = await fetch(
        `data:${attachment.DocType};base64,${attachment.Data}`
      );
      const blob = await response.blob();

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = attachment.Name + attachment.DocType;
      link.click();
      URL.revokeObjectURL(link.href);
    } else {
      const downloadPromise = api.getDownload("DownloadEirDoc", {
        fileUrl: attachment.FileUrl,
        fileName: attachment.Name,
      });

      await handleResponsePromiseAsync(
        downloadPromise,
        () => {},
        "FormAttachmentsView.vue",
        "downloadAttachmentAsync",
        () => {
          notificationStore.notify({
            type: NotificationTypeEnum.error,
            message: `Error downloading ${attachment.Name}`,
            duration: 5000,
          });
        }
      );
    }
  } catch (error) {
    notificationStore.notify({
      type: NotificationTypeEnum.error,
      message: `Error downloading ${attachment.Name}`,
      duration: 5000,
    });
  }
  downloadingAttachment.value = null;
};

const deleteAttachmentAsync = async function (attachment: FormAttachement) {
  await handleResponsePromiseAsync(
    api.post("DeleteSavedEirDoc", {
      VisitId: props.visitId,
      FileName: attachment.Name + attachment.DocType,
    }),
    (data) => {
      if (data)
        existingAttachments.value.splice(
          existingAttachments.value.indexOf(attachment),
          1
        );
    },
    "FormAttachmentsView.vue",
    "deleteAttachmentAsync",
    () => {
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: `Error deleting ${attachment.Name}`,
        duration: 5000,
      });
    }
  );
};

onBeforeMount(async () => {
  await updateFormAttachmentsAsync();
});
</script>

<style lang="scss">
.ingredient-attachments-view {
  display: flex;
  flex-direction: column;
  width: 100%;

  &__header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    font-size: 14px;
  }

  &__attachments {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    overflow-y: auto;
  }

  &__attachment {
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
    margin-bottom: 1rem;
    padding: 0.5rem 1rem;
    border: 1px solid #ccc;
    border-radius: 0.5rem;
    flex-wrap: wrap;
    gap: 4px;

    &__date {
      font-weight: bold;
    }
  }
}
</style>
